import type {
  AdjustMoneyInput,
  AdjustResourceCommandInput,
  BazaarExchangeInput,
  ResolveEventCardInput,
  TransferResourceInput,
  CreateGameSessionInput,
  DemoFeedbackInput,
  DemoFeedbackView,
  DemoOfferKind,
  GameDetail,
  GameSessionSummary,
  GameSessionView,
  GameSummary,
  GuestSessionCredentials,
  JoinGameSessionResponse,
  JoinSessionInput,
} from '@creativitas/shared';
import { apiFetch } from '@/lib/api';

export const GAMES_KEY = ['games'] as const;
export const GAME_SESSIONS_KEY = ['game-sessions'] as const;
export const gameKey = (id: string) => ['games', id] as const;
export const gameSessionKey = (id: string) => ['game-sessions', id] as const;

export const fetchGames = (): Promise<{ items: GameSummary[] }> =>
  apiFetch<{ items: GameSummary[] }>('/games');
export const fetchGame = (id: string): Promise<GameDetail> => apiFetch<GameDetail>(`/games/${id}`);
export const createGameSession = (
  gameId: string,
  input: CreateGameSessionInput = {},
): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/games/${gameId}/sessions`, { method: 'POST', body: input });
export const fetchGameSessions = (): Promise<{ items: GameSessionSummary[] }> =>
  apiFetch<{ items: GameSessionSummary[] }>('/game-sessions');
/** Отмена своей сессии в «Ожидании» — общий адрес для партий и МАК-сессий. */
export const cancelWaitingSession = (sessionId: string): Promise<{ cancelled: true; id: string }> =>
  apiFetch<{ cancelled: true; id: string }>(`/sessions/${sessionId}`, { method: 'DELETE' });
export const fetchGameSession = (sessionId: string): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/game-sessions/${sessionId}`);

const sessionPost = (sessionId: string, command: string, body?: unknown): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/game-sessions/${sessionId}/${command}`, {
    method: 'POST',
    ...(body === undefined ? {} : { body }),
  });

export const startGameSession = (id: string) => sessionPost(id, 'start');
/* «Играть самостоятельно»: ведущий занимает место за собственным столом. */
export const seatHostAtGameTable = (id: string) => sessionPost(id, 'seat');
/* Ведущий сам решает, когда закрыть комнату: войти можно и в идущую партию. */
export const setGameEntryOpen = (id: string, open: boolean) =>
  sessionPost(id, 'entry', { open });
/* GAME-31/32 — вступление и сказки секторов ведёт ведущий. */
export const openSectorStory = (id: string, sectorId: string) =>
  sessionPost(id, 'story', { sectorId });
export const closeSectorStory = (id: string) => sessionPost(id, 'story/close');
export const drawStartTalent = (id: string, participantId: string) =>
  sessionPost(id, 'start-talent', { participantId });
export const rollGameDice = (id: string, participantId: string) =>
  sessionPost(id, 'dice', { participantId });
export const drawGameCard = (id: string, participantId: string) =>
  sessionPost(id, 'cards', { participantId });
export const revealGameCard = (id: string, cardId: string) =>
  sessionPost(id, `cards/${cardId}/reveal`);
export const adjustGameResource = (id: string, input: AdjustResourceCommandInput) =>
  sessionPost(id, 'resources', input);
export const adjustGameMoney = (id: string, input: AdjustMoneyInput) =>
  sessionPost(id, 'money', input);
/* GAME-35/36/39 — подарок, обмен на Базаре и закрытие карты события. */
export const transferGameResource = (id: string, input: TransferResourceInput) =>
  sessionPost(id, 'transfer', input);
export const exchangeAtBazaar = (id: string, input: BazaarExchangeInput) =>
  sessionPost(id, 'bazaar', input);
export const drawGrantedTalent = (id: string, participantId: string) =>
  sessionPost(id, 'granted-talent', { participantId });
export const resolveEventCard = (id: string, input: ResolveEventCardInput) =>
  sessionPost(id, 'cards/resolve', input);
/* Демо «для себя»: игрок отмечает выполненное задание; награду назначает сервер. */
export const confirmSoloTask = (id: string, cardId: string) =>
  sessionPost(id, `cards/${cardId}/confirm`);
export const pauseGameSession = (id: string) => sessionPost(id, 'pause');
export const resumeGameSession = (id: string) => sessionPost(id, 'resume');
export const completeGameSession = (id: string) => sessionPost(id, 'complete');
export const undoGameAction = (id: string) => sessionPost(id, 'undo');

/*
  Итог демо (docs/ANALYTICS.md). Нажатие кнопки предложения отмечается
  попутно: переход не ждёт ответа и не срывается из-за ошибки, а `keepalive`
  даёт запросу дойти, даже если страница уже уходит.
*/
export const reportDemoOfferClick = (id: string): void => {
  void apiFetch<{ kind: DemoOfferKind }>(`/game-sessions/${id}/demo/offer`, { method: 'POST', keepalive: true })
    .catch(() => undefined);
};
export const answerDemoFeedback = (id: string, input: DemoFeedbackInput): Promise<DemoFeedbackView> =>
  apiFetch<DemoFeedbackView>(`/game-sessions/${id}/demo/feedback`, { method: 'PUT', body: input });
export const removeGameParticipant = (id: string, participantId: string) =>
  apiFetch<GameSessionView>(`/game-sessions/${id}/participants/${participantId}`, { method: 'DELETE' });

export const joinGameSession = (token: string, input: JoinSessionInput): Promise<JoinGameSessionResponse> =>
  apiFetch<JoinGameSessionResponse>(`/invitations/${token}/join`, { method: 'POST', body: input });

const guestHeaders = (credentials: GuestSessionCredentials) => ({
  Authorization: `Bearer ${credentials.guestToken}`,
});
export const fetchGuestGameSession = (credentials: GuestSessionCredentials): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/guest-game-sessions/${credentials.sessionId}`, {
    headers: guestHeaders(credentials),
  });
export const drawGuestStartTalent = (credentials: GuestSessionCredentials): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/guest-game-sessions/${credentials.sessionId}/start-talent`, {
    method: 'POST',
    headers: guestHeaders(credentials),
  });
export const drawGuestGrantedTalent = (credentials: GuestSessionCredentials): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/guest-game-sessions/${credentials.sessionId}/granted-talent`, {
    method: 'POST',
    headers: guestHeaders(credentials),
  });
export const rollGuestGameDice = (credentials: GuestSessionCredentials): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/guest-game-sessions/${credentials.sessionId}/dice`, {
    method: 'POST',
    headers: guestHeaders(credentials),
  });
export const drawGuestGameCard = (credentials: GuestSessionCredentials): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(`/guest-game-sessions/${credentials.sessionId}/cards`, {
    method: 'POST',
    headers: guestHeaders(credentials),
  });
export const revealGuestGameCard = (
  credentials: GuestSessionCredentials,
  cardId: string,
): Promise<GameSessionView> =>
  apiFetch<GameSessionView>(
    `/guest-game-sessions/${credentials.sessionId}/cards/${cardId}/reveal`,
    { method: 'POST', headers: guestHeaders(credentials) },
  );
